import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { sanitizeSearchInput } from '../../utils/security';
import { fetchCategories } from '../../services/categoriesService';
import {
  buildProductCategoryTree,
  foldedSearchTokens,
  productMatchesFoldedTokens,
} from '../../utils/productPickerCore';
import { FoldedSearchHighlight } from '../FoldedSearchHighlight';
import { CursorTooltip } from '../CursorTooltip';
import IntegerInput from '../IntegerInput';
import './StokLimitliParcaPicker.css';

const getCategoryId = (c) => String(c?.id || c?._id || '');

function collectDescendantIds(categoryId, flatCategories) {
  const ids = new Set([String(categoryId)]);
  const children = flatCategories.filter(
    (c) => c.ustKategoriId && String(c.ustKategoriId) === String(categoryId),
  );
  for (const child of children) {
    collectDescendantIds(getCategoryId(child), flatCategories).forEach((id) => ids.add(id));
  }
  return ids;
}

const DRAFT_CAT = '__draft__';

const getName = (p) => p.parcaAdi || p.icerikAdi || '—';
const getCode = (p) => p.parcaKodu || p.icerikKodu || '—';

function StockBadge({ stokMusait, ozAdet, konsinyeAdet }) {
  const hasSplit =
    (ozAdet != null && ozAdet > 0) || (konsinyeAdet != null && konsinyeAdet > 0);

  if (!hasSplit) {
    return (
      <div className="stok-badge-cell">
        <span className="stok-badge">{stokMusait ?? 0}</span>
      </div>
    );
  }

  const hintTooltip = `Öz: ${ozAdet ?? 0} · Konsinye: ${konsinyeAdet ?? 0}`;

  const cell = (
    <div className="stok-badge-cell">
      <span className="stok-badge">{stokMusait ?? 0}</span>
      <span className="stok-badge-split">
        {ozAdet > 0 ? <span className="stok-badge-oz">Ö:{ozAdet}</span> : null}
        {konsinyeAdet > 0 ? <span className="stok-badge-konsinye">K:{konsinyeAdet}</span> : null}
      </span>
    </div>
  );

  return (
    <CursorTooltip content={hintTooltip} className="stok-badge-tooltip">
      {cell}
    </CursorTooltip>
  );
}

/**
 * İade oluştur: yalnızca stokta olan parçalar
 */
const StokLimitliParcaPicker = ({
  katalog = [],
  loading = false,
  secilenParcalar = [],
  onSecilenChange,
  showSelectedSummary = true,
}) => {
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [expandedIds, setExpandedIds] = useState({});
  const [parcaArama, setParcaArama] = useState('');
  const [parcaAramaDebounced, setParcaAramaDebounced] = useState('');
  const [qtyById, setQtyById] = useState({});

  useEffect(() => {
    const t = setTimeout(() => {
      setParcaAramaDebounced(sanitizeSearchInput(parcaArama, { trimEdges: false }) || '');
    }, 300);
    return () => clearTimeout(t);
  }, [parcaArama]);

  useEffect(() => {
    fetchCategories({ kategoriTipi: 'parca' })
      .then((res) => {
        const cats = res?.veri?.kategoriler || res?.veri || [];
        setCategories(Array.isArray(cats) ? cats : []);
      })
      .catch(() => setCategories([]));
  }, []);

  const categoryTree = useMemo(() => buildProductCategoryTree(categories), [categories]);

  useEffect(() => {
    if (categoryTree.length === 0) return;
    setExpandedIds((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const next = {};
      categoryTree.forEach((r) => {
        next[r.cid] = true;
      });
      return next;
    });
  }, [categoryTree]);

  const katalogById = useMemo(() => {
    const m = {};
    for (const p of katalog) {
      if (p.icerikId) m[String(p.icerikId)] = p;
    }
    return m;
  }, [katalog]);

  const filteredParcalar = useMemo(() => {
    let list = [...katalog];
    const tokens = foldedSearchTokens(parcaAramaDebounced);
    if (tokens.length) {
      list = list.filter((p) =>
        productMatchesFoldedTokens(
          { icerikAdi: p.parcaAdi, icerikKodu: p.parcaKodu, parcaAdi: p.parcaAdi, parcaKodu: p.parcaKodu },
          tokens,
        ),
      );
    }
    if (selectedCategoryId === DRAFT_CAT) {
      const ids = new Set(secilenParcalar.map((p) => String(p.icerikId)));
      list = list.filter((p) => ids.has(String(p.icerikId)));
    } else if (selectedCategoryId) {
      const allowed = collectDescendantIds(selectedCategoryId, categories);
      list = list.filter((p) => allowed.has(String(p.kategoriId || '')));
    }
    return list.sort((a, b) =>
      getName(a).localeCompare(getName(b), 'tr', { sensitivity: 'base' }),
    );
  }, [katalog, parcaAramaDebounced, selectedCategoryId, categories, secilenParcalar]);

  const secilenById = useMemo(() => {
    const m = {};
    for (const p of secilenParcalar) {
      m[String(p.icerikId)] = p;
    }
    return m;
  }, [secilenParcalar]);

  const toplamTutar = useMemo(
    () =>
      secilenParcalar.reduce(
        (acc, p) => acc + (Number(p.fiyatgaranti ?? p.fiyat ?? 0) || 0) * (p.adet || 1),
        0,
      ),
    [secilenParcalar],
  );

  const getMaxAdet = useCallback(
    (icerikId) => {
      const item = katalogById[String(icerikId)];
      if (!item) return 0;
      const already = secilenById[String(icerikId)]?.adet || 0;
      return Math.max(0, (item.kullanilabilir ?? 0) - already);
    },
    [katalogById, secilenById],
  );

  const handleAdd = useCallback(
    (parca, adetRaw) => {
      const icerikId = String(parca.icerikId);
      const maxAdd = getMaxAdet(icerikId);
      if (maxAdd <= 0) return;
      const adet = Math.min(maxAdd, Math.max(1, parseInt(adetRaw, 10) || 1));
      const existing = secilenById[icerikId];
      const fiyat = parca.fiyatgaranti ?? parca.fiyat;
      if (existing) {
        const newAdet = Math.min(
          parca.kullanilabilir ?? 0,
          (existing.adet || 0) + adet,
        );
        if (newAdet <= 0) return;
        onSecilenChange?.(
          secilenParcalar.map((p) =>
            String(p.icerikId) === icerikId ? { ...p, adet: newAdet } : p,
          ),
        );
      } else {
        onSecilenChange?.([
          ...secilenParcalar,
          {
            icerikId,
            parcaAdi: parca.parcaAdi,
            parcaKodu: parca.parcaKodu,
            fiyatgaranti: fiyat,
            adet,
          },
        ]);
      }
    },
    [getMaxAdet, secilenById, secilenParcalar, onSecilenChange],
  );

  const handleRemove = (icerikId) => {
    onSecilenChange?.(secilenParcalar.filter((p) => String(p.icerikId) !== String(icerikId)));
  };

  const handleAdetChange = (icerikId, raw) => {
    const item = katalogById[String(icerikId)];
    const max = item?.kullanilabilir ?? 0;
    const num = Math.min(max, Math.max(1, parseInt(raw, 10) || 1));
    onSecilenChange?.(
      secilenParcalar.map((p) =>
        String(p.icerikId) === String(icerikId) ? { ...p, adet: num } : p,
      ),
    );
  };

  const toggleExpand = (cid, e) => {
    e.stopPropagation();
    setExpandedIds((prev) => ({ ...prev, [cid]: !prev[cid] }));
  };

  const renderCategoryNode = (node, depth = 0) => {
    const cid = node.cid;
    const hasChildren = (node.children || []).length > 0;
    const isExpanded = expandedIds[cid] === true;
    const isSelected = selectedCategoryId === cid;

    return (
      <div key={cid} className="siparis-parca-picker__cat-node" style={{ paddingLeft: depth * 10 }}>
        <div
          className={`siparis-parca-picker__cat-row ${isSelected ? 'is-selected' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => setSelectedCategoryId(cid)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setSelectedCategoryId(cid);
            }
          }}
        >
          {hasChildren ? (
            <button
              type="button"
              className="siparis-parca-picker__cat-toggle"
              onClick={(e) => toggleExpand(cid, e)}
              aria-label={isExpanded ? 'Daralt' : 'Genişlet'}
            >
              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} />
            </button>
          ) : (
            <span className="siparis-parca-picker__cat-toggle-spacer" />
          )}
          <span className="siparis-parca-picker__cat-name">{node.kategoriAdi || '—'}</span>
        </div>
        {hasChildren && isExpanded && (
          <div className="siparis-parca-picker__cat-children">
            {node.children.map((ch) => renderCategoryNode(ch, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="siparis-parca-picker__loading stok-limitli-parca-picker">
        <i className="fas fa-spinner fa-spin" /> Stoktaki parçalar yükleniyor...
      </div>
    );
  }

  return (
    <div className="siparis-parca-picker stok-limitli-parca-picker">
      {showSelectedSummary && secilenParcalar.length > 0 && (
        <div className="stok-limitli-parca-picker__selected-bar">
          <div>
            <strong>{secilenParcalar.length}</strong> parça seçildi · Tahmini{' '}
            <strong>{toplamTutar.toFixed(2)} ₺</strong>
          </div>
          <div className="stok-limitli-parca-picker__selected-chips">
            {secilenParcalar.map((p) => (
              <span key={p.icerikId} className="stok-limitli-parca-picker__chip">
                {p.parcaAdi} ×{p.adet}
                <button type="button" onClick={() => handleRemove(p.icerikId)} aria-label="Kaldır">
                  <i className="fas fa-times" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="siparis-parca-picker__search-wrap">
        <i className="fas fa-search search-icon" />
        <input
          type="text"
          className="siparis-parca-picker__search-input"
          placeholder="Parça adı veya kod ile ara..."
          value={parcaArama}
          onChange={(e) => setParcaArama(e.target.value)}
          maxLength={100}
        />
      </div>

      <div className="stok-limitli-parca-picker__layout">
        <div className="siparis-parca-picker__categories-panel">
          <div className="siparis-parca-picker__cat-header">Kategoriler</div>
          <div className="siparis-parca-picker__cat-toolbar">
            <button
              type="button"
              className={`siparis-parca-picker__cat-all ${!selectedCategoryId ? 'is-selected' : ''}`}
              onClick={() => setSelectedCategoryId(null)}
            >
              <i className="fas fa-layer-group" />
              Tüm parçalar
            </button>
            {secilenParcalar.length > 0 ? (
              <button
                type="button"
                className={`siparis-parca-picker__cat-all ${selectedCategoryId === DRAFT_CAT ? 'is-selected' : ''}`}
                onClick={() => setSelectedCategoryId(DRAFT_CAT)}
              >
                <i className="fas fa-file-alt" />
                Taslaklar ({secilenParcalar.length})
              </button>
            ) : null}
          </div>
          <div className="siparis-parca-picker__cat-tree">
            {categoryTree.map((node) => renderCategoryNode(node))}
          </div>
        </div>

        <div className="siparis-parca-picker__parts">
          <div className="siparis-parca-picker__parts-header">
            <span>Stoktaki parçalar</span>
            <span className="siparis-parca-picker__parts-count">{filteredParcalar.length} kayıt</span>
          </div>
          <div className="siparis-parca-picker__table-wrap">
            {filteredParcalar.length === 0 ? (
              <div className="siparis-parca-picker__empty">
                Stokta iade edilebilir parça bulunamadı
              </div>
            ) : (
              <table className="admins-table siparis-parca-picker__table siparis-parca-picker__table--with-price">
                <thead>
                  <tr>
                    <th>Parça</th>
                    <th>Kod</th>
                    <th style={{ textAlign: 'right' }}>Stok</th>
                    <th style={{ textAlign: 'right' }}>Net fiyat</th>
                    <th style={{ textAlign: 'center' }}>Adet</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filteredParcalar.map((p) => {
                    const pid = String(p.icerikId);
                    const disabled = (p.kullanilabilir ?? 0) <= 0;
                    const maxQty = p.kullanilabilir ?? 0;
                    return (
                      <tr key={pid} className={disabled ? 'is-disabled' : ''}>
                        <td title={getName(p)}>
                          <FoldedSearchHighlight text={getName(p)} queryRaw={parcaAramaDebounced} />
                        </td>
                        <td title={getCode(p)}>
                          <FoldedSearchHighlight text={getCode(p)} queryRaw={parcaAramaDebounced} />
                        </td>
                        <td className="stok-col-cell" style={{ textAlign: 'right' }}>
                          <StockBadge
                            stokMusait={p.stokMusait ?? p.adet ?? 0}
                            ozAdet={p.ozAdet}
                            konsinyeAdet={p.konsinyeAdet}
                          />
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {p.fiyatgaranti != null
                            ? `${parseFloat(p.fiyatgaranti).toFixed(2)} ₺`
                            : '—'}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <IntegerInput
                            min={1}
                            max={maxQty || 1}
                            disabled={disabled}
                            className="siparis-parca-picker__qty"
                            value={qtyById[pid] ?? 1}
                            onChange={(v) =>
                              setQtyById((prev) => ({ ...prev, [pid]: v }))
                            }
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="category-action-btn siparis-parca-picker__add-btn"
                            disabled={disabled}
                            onClick={() => handleAdd(p, qtyById[pid] ?? 1)}
                            title="Ekle"
                          >
                            <i className="fas fa-plus" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StokLimitliParcaPicker;
